import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

const KEY = "bs_cookie_consent_v1";

export default function CookieConsent() {
  const [show, setShow] = useState(false);
  const [expand, setExpand] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false, preferences: false });

  useEffect(() => {
    if (!localStorage.getItem(KEY)) setShow(true);
  }, []);

  const save = async (choice) => {
    const value = choice || prefs;
    localStorage.setItem(KEY, JSON.stringify(value));
    try { await api.post("/consent", value); } catch { /* non-blocking */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6" data-testid="cookie-consent">
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bs-glass p-5 shadow-soft">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-xl bg-primary/15 p-2 text-primary"><Cookie className="h-5 w-5" /></div>
          <div className="flex-1">
            <p className="text-sm font-semibold">We value your privacy</p>
            <p className="mt-1 text-sm text-muted-foreground">
              We use necessary cookies to run the site. Optional cookies load only with your consent.
              See our <Link to="/cookie-policy" className="text-primary underline">Cookie Policy</Link>.
            </p>

            {expand && (
              <div className="mt-4 space-y-3" data-testid="cookie-options">
                {[
                  ["necessary", "Necessary", "Required for security and login.", true],
                  ["preferences", "Preferences", "Remember choices like your theme.", false],
                  ["analytics", "Analytics", "Help us understand usage.", false],
                  ["marketing", "Marketing", "Measure campaign performance.", false],
                ].map(([k, label, desc, locked]) => (
                  <div key={k} className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card/60 px-3 py-2.5">
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                    <Switch checked={prefs[k]} disabled={locked}
                      onCheckedChange={(v) => setPrefs((p) => ({ ...p, [k]: v }))}
                      data-testid={`cookie-switch-${k}`} />
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => save({ necessary: true, analytics: true, marketing: true, preferences: true })} data-testid="cookie-accept-all">Accept all</Button>
              <Button size="sm" variant="outline" onClick={() => save({ necessary: true, analytics: false, marketing: false, preferences: false })} data-testid="cookie-reject">Reject optional</Button>
              {expand
                ? <Button size="sm" variant="ghost" onClick={() => save()} data-testid="cookie-save">Save choices</Button>
                : <Button size="sm" variant="ghost" onClick={() => setExpand(true)} data-testid="cookie-customize">Customize</Button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
